import React from 'react';
import { useParams } from 'react-router';
import { NavLink } from 'react-router-dom';

import { Formik, FormikErrors, Form as FormikForm, FieldArray } from 'formik';

import { Alert, Breadcrumb, Button, Panel } from '@shared/components/bootstrap';
import { FormikCheckboxFormGroup } from '@shared/components/fields/formik/formik_checkbox_form_group';
import { FormikFilesFormGroup } from '@shared/components/fields/formik/formik_files_form_group';
import { FormikInputFormGroup } from '@shared/components/fields/formik/formik_input_form_group';
import { FormikMultiselectFormGroup } from '@shared/components/fields/formik/formik_multiselect_form_group';

import { Spinner } from '@admin/components/spinner';
import { usePermission } from '@admin/hooks/use_permission';
import { useTitle } from '@admin/hooks/use_title';
import { TRAINING_UPLOAD } from '@admin/libraries/access_control_catalog.generated';
import { client } from '@admin/libraries/apollo';
import {
  Status,
  useTrainingUploadQuery,
  useTrainingUploadTeamsQuery,
  useTrainingUploadCreateMutation,
  useTrainingUploadUpdateMutation,
  useTrainingUploadStatusUpdateMutation,
} from '@admin/schema';

import { FormikRadioFormGroup } from '../fields/formik/formik_radio_form_group';

type QuestionValues = {
  id?: string;
  text: string;
  explanation: string;
  options: Array<{ text: string; correct: boolean }>;
};

type FormValues = {
  fileSignedIDs: string[];
  name: string;
  questions: QuestionValues[];
  teamIDs: string[];
  teamSelectionOption: string;
};

export const TrainingUploadForm: React.FC = () => {
  const { id } = useParams<{ id?: string }>();
  const isEdit = !!id;
  useTitle(isEdit ? 'Edit Training Upload' : 'New Training Upload');
  const canCreateTrainingUploads = usePermission(TRAINING_UPLOAD.resource, TRAINING_UPLOAD.actions.create);
  const canUpdateTrainingUploads = usePermission(TRAINING_UPLOAD.resource, TRAINING_UPLOAD.actions.update);
  const canManageTrainingUploads = isEdit ? canUpdateTrainingUploads : canCreateTrainingUploads;

  const { data: uploadData, loading: queryLoading } = useTrainingUploadQuery({
    client,
    variables: { id: id! },
    skip: !id,
  });

  const { data: teamsData, loading: teamsLoading } = useTrainingUploadTeamsQuery({ client });

  const [createUpload, { loading: creating, data: createData }] = useTrainingUploadCreateMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.status === Status.Ok) {
        window.location.href = '/training_uploads';
      }
    },
  });

  const [updateUpload, { loading: updating, data: updateData }] = useTrainingUploadUpdateMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.status === Status.Ok) {
        window.location.href = '/training_uploads';
      }
    },
  });

  const [updateStatus, { loading: archiving, data: statusData }] = useTrainingUploadStatusUpdateMutation({
    client,
    onCompleted: ({ result }) => {
      if (result?.status === Status.Ok) {
        window.location.href = '/training_uploads';
      }
    },
  });

  const teamOptions = teamsData?.teams ?? [];
  const trainingUpload = uploadData?.trainingUpload;

  const mutationError = statusData?.result?.error ?? (isEdit ? updateData?.result?.error : createData?.result?.error);

  if (!canManageTrainingUploads) {
    return (
      <>
        <h1 className="page-header text-overflow">
          <Breadcrumb>
            <Breadcrumb.Item>
              <NavLink to="/training_uploads">Training Uploads</NavLink>
            </Breadcrumb.Item>
            <Breadcrumb.Item active>{isEdit ? 'Edit' : 'New'}</Breadcrumb.Item>
          </Breadcrumb>
        </h1>
        <Alert style="danger">You do not have permission to create or edit training uploads.</Alert>
      </>
    );
  }

  if (isEdit && queryLoading) {
    return <Spinner />;
  }

  const existingAttachments = trainingUpload?.attachments ?? [];
  const existingTeamIDs = trainingUpload?.teams.map((t) => t.id) ?? [];
  const isArchived = !!trainingUpload?.deletedAt;

  const initialQuestions: QuestionValues[] = (trainingUpload?.questions ?? []).map((q) => ({
    id: q.id,
    text: q.text,
    explanation: q.explanation ?? '',
    options: q.options.map((opt) => ({
      text: opt,
      correct: q.correctOptions.includes(opt),
    })),
  }));

  const initialValues: FormValues = {
    fileSignedIDs: existingAttachments.map((a) => a.signedID),
    name: trainingUpload?.name ?? '',
    questions: initialQuestions,
    teamIDs: existingTeamIDs,
    teamSelectionOption: isEdit ? 'custom' : 'all',
  };

  const serializeQuestions = (questions: QuestionValues[]) =>
    questions.map((q) => ({
      id: q.id ?? null,
      text: q.text,
      explanation: q.explanation || null,
      options: q.options.map((o) => o.text),
      correctOptions: q.options.filter((o) => o.correct).map((o) => o.text),
    }));

  const resolveTeamIDs = (values: FormValues): string[] => {
    switch (values.teamSelectionOption) {
      case 'all':
        return teamOptions.filter((t) => t.name.includes('Field') || t.name.includes('Warehouse')).map((t) => t.id);
      case 'field':
        return teamOptions.filter((t) => t.name.includes('Field')).map((t) => t.id);
      case 'warehouse':
        return teamOptions.filter((t) => t.name.includes('Warehouse')).map((t) => t.id);
      default:
        return values.teamIDs;
    }
  };

  const onSubmit = (values: FormValues) => {
    const teamIDs = resolveTeamIDs(values);
    const questions = serializeQuestions(values.questions);

    if (isEdit) {
      updateUpload({
        variables: {
          input: {
            trainingUploadID: id!,
            fileSignedIDs: values.fileSignedIDs,
            name: values.name,
            questions,
            teamIDs,
          },
        },
      });
    } else {
      createUpload({
        variables: {
          input: {
            fileSignedIDs: values.fileSignedIDs,
            name: values.name,
            questions,
            teamIDs,
          },
        },
      });
    }
  };

  const validate = (values: FormValues): FormikErrors<FormValues> => {
    const errors: FormikErrors<FormValues> = {};
    if (values.fileSignedIDs.length === 0) errors.fileSignedIDs = 'Please select at least one file';
    if (values.name.length === 0) errors.name = 'Please enter a name';
    if (values.teamSelectionOption === 'custom') {
      if (values.teamIDs.length === 0) errors.teamIDs = 'Please select at least one team';
    } else {
      const resolved = resolveTeamIDs(values);
      if (resolved.length === 0) errors.teamIDs = 'Teams are still loading, please wait';
    }
    return errors;
  };

  const saving = creating || updating || teamsLoading;

  return (
    <>
      <h1 className="page-header text-overflow">
        <Breadcrumb>
          <Breadcrumb.Item>
            <NavLink to="/training_uploads">Training Uploads</NavLink>
          </Breadcrumb.Item>
          <Breadcrumb.Item active>{isEdit ? (trainingUpload?.name ?? 'Edit') : 'New'}</Breadcrumb.Item>
        </Breadcrumb>
      </h1>

      <Formik<FormValues> initialValues={initialValues} onSubmit={onSubmit} validate={validate} enableReinitialize>
        {({ values }) => (
          <FormikForm>
            <Panel>
              <Panel.Body>
                {mutationError && <Alert style="danger">{mutationError}</Alert>}

                <FormikInputFormGroup label="Name" name="name" />

                <FormikRadioFormGroup
                  label="Teams"
                  id="teamSelectionOption"
                  name="teamSelectionOption"
                  options={[
                    { value: 'all', label: 'Field & Warehouse' },
                    { value: 'field', label: 'Field Only' },
                    { value: 'warehouse', label: 'Warehouse Only' },
                    { value: 'custom', label: 'Custom' },
                  ]}
                />

                {values.teamSelectionOption === 'custom' && (
                  <FormikMultiselectFormGroup
                    label=""
                    name="teamIDs"
                    entries={teamOptions.map((t) => ({ id: t.id, name: t.name }))}
                  />
                )}

                <FormikFilesFormGroup
                  id="fileSignedIDs"
                  name="fileSignedIDs"
                  label="Files (PDF, Video, Image)"
                  accept="application/pdf,video/*,image/*"
                  initial={existingAttachments.map((a) => ({
                    id: a.id,
                    filename: a.filename,
                    signedID: a.signedID,
                    url: a.url,
                  }))}
                />

                <hr />

                <h4>Quiz Questions</h4>
                <p className="text-muted" style={{ marginBottom: '16px' }}>
                  Optional. Adding questions enables a comprehension quiz for this training.
                </p>

                <FieldArray name="questions">
                  {(questionHelpers) => (
                    <div>
                      {values.questions.map((question, qIdx) => (
                        <div
                          key={qIdx}
                          style={{
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            padding: '16px',
                            marginBottom: '16px',
                            background: '#fafafa',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              marginBottom: '8px',
                            }}
                          >
                            <strong>Question {qIdx + 1}</strong>
                            <Button type="button" kind="danger" onClick={() => questionHelpers.remove(qIdx)}>
                              Remove Question
                            </Button>
                          </div>

                          <FormikInputFormGroup name={`questions.${qIdx}.text`} label="Question Text" />

                          <FormikInputFormGroup
                            name={`questions.${qIdx}.explanation`}
                            label="Correct Answer Explanation (optional)"
                            placeholder="Shown to users who answer incorrectly"
                          />

                          <FieldArray name={`questions.${qIdx}.options`}>
                            {(optionHelpers) => (
                              <div>
                                {question.options.length > 0 && <label>Options</label>}
                                {question.options.map((_, oIdx) => (
                                  <div
                                    key={oIdx}
                                    style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '16px' }}
                                  >
                                    <FormikInputFormGroup
                                      name={`questions.${qIdx}.options.${oIdx}.text`}
                                      style={{ flex: 1 }}
                                    />
                                    <FormikCheckboxFormGroup
                                      id={`question-${qIdx}-option-${oIdx}-correct`}
                                      name={`questions.${qIdx}.options.${oIdx}.correct`}
                                      label="Correct"
                                      inlineLabel
                                    />
                                    <Button type="button" kind="danger" onClick={() => optionHelpers.remove(oIdx)}>
                                      Remove
                                    </Button>
                                  </div>
                                ))}
                                <Button
                                  type="button"
                                  kind="default"
                                  onClick={() => optionHelpers.push({ text: '', correct: false })}
                                >
                                  + Add Option
                                </Button>
                              </div>
                            )}
                          </FieldArray>
                        </div>
                      ))}

                      <Button
                        type="button"
                        kind="default"
                        onClick={() => questionHelpers.push({ text: '', explanation: '', options: [] })}
                      >
                        + Add Question
                      </Button>
                    </div>
                  )}
                </FieldArray>
              </Panel.Body>
              <Panel.Footer align="right">
                {isEdit && (
                  <Button
                    type="button"
                    kind="warning"
                    loading={archiving}
                    onClick={() => {
                      const action = isArchived ? 'unarchive' : 'archive';
                      if (window.confirm(`Are you sure you want to ${action} this training upload?`)) {
                        updateStatus({
                          variables: {
                            input: {
                              trainingUploadID: id!,
                              active: isArchived,
                            },
                          },
                        });
                      }
                    }}
                    style={{ float: 'left' }}
                  >
                    {isArchived ? 'Unarchive' : 'Archive'}
                  </Button>
                )}
                <Button
                  type="button"
                  kind="default"
                  onClick={() => {
                    window.location.href = '/training_uploads';
                  }}
                >
                  Cancel
                </Button>
                <Button loading={saving} kind="primary" type="submit">
                  {isEdit ? 'Save Changes' : 'Create Training Upload'}
                </Button>
              </Panel.Footer>
            </Panel>
          </FormikForm>
        )}
      </Formik>
    </>
  );
};
